import { useEffect, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import type { ECharts as EChartsInstance, EChartsOption, SeriesOption } from "echarts";

import type { Extent, Viewport } from "../../lib/chartViewport";
import { DownsampleCache, downsampleSeries, makeSeriesVersion, type XYPoint } from "../../utils/downsample";

export type TimeSeriesPoint = { t: number | string | Date; v: number };

export type TimeSeriesSeries = {
  id: string;
  name: string;
  color?: string;
  data: TimeSeriesPoint[];
};

type Props = {
  series: TimeSeriesSeries[];
  height?: number;
  yLabel?: string;
  gridBottom?: number;
  yDomain?: readonly [number, number];
  safeBand?: {
    min: number;
    max: number;
    color?: string;
    opacity?: number;
  };
  extent?: Extent | null;
  viewport?: Viewport | null;
  onViewportChange?: (next: Viewport) => void;
  unitLabel?: string;
  tooltipFontSize?: number;
  showLegend?: boolean;
  xAxisType?: "time" | "value";
  xAxisName?: string;
  tooltipXFormatter?: (value: number) => string;
  dataZoomSlider?: {
    bottom?: number;
    height?: number;
    fillerColor?: string;
    backgroundColor?: string;
    borderColor?: string;
    dataBackgroundLineColor?: string;
    dataBackgroundAreaColor?: string;
    selectedDataBackgroundLineColor?: string;
    selectedDataBackgroundAreaColor?: string;
    handleColor?: string;
    handleBorderColor?: string;
    moveHandleColor?: string;
  };
};

type TooltipItem = {
  value: [number, number];
  color: string;
  seriesName: string;
};

type DataZoomPayload = {
  batch?: DataZoomPayload[];
  startValue?: number;
  endValue?: number;
};

type NormalizedSeries = {
  id: string;
  name: string;
  color?: string;
  points: XYPoint[];
  version: string;
};

const fallbackFormatter = new Intl.DateTimeFormat("fr-FR", {
  dateStyle: "medium",
  timeStyle: "short",
});
const numberFormatter = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const clampTargetPoints = (width: number) => {
  if (!Number.isFinite(width) || width <= 0) return 800;
  return Math.min(Math.max(Math.round(width * 1.5), 500), 5000);
};

const areViewportsClose = (a: Viewport | null, b: Viewport | null) => {
  if (!a || !b) return false;
  return Math.abs(a.start - b.start) < 1 && Math.abs(a.end - b.end) < 1;
};

const toTimestamp = (value: number | string | Date) => {
  if (typeof value === "number") return value;
  if (value instanceof Date) return value.getTime();
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : NaN;
};

function normalizeSeries(list: TimeSeriesSeries[]): NormalizedSeries[] {
  return list.map((serie) => {
    const points: XYPoint[] = [];
    serie.data.forEach((row) => {
      const x = toTimestamp(row.t);
      const y = Number(row.v);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;
      points.push({ x, y });
    });
    points.sort((a, b) => a.x - b.x);
    return {
      id: serie.id,
      name: serie.name,
      color: serie.color,
      points,
      version: makeSeriesVersion(points),
    };
  });
}

function countVisible(points: XYPoint[], viewport: Viewport | null) {
  if (!viewport) return points.length;
  const { start, end } = viewport;
  if (points.length === 0) return 0;

  let lo = 0;
  let hi = points.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (points[mid].x < start) lo = mid + 1;
    else hi = mid;
  }
  const from = lo;

  lo = from;
  hi = points.length - 1;
  while (lo < hi) {
    const mid = 1 + ((lo + hi) >> 1);
    if (points[mid].x > end) hi = mid - 1;
    else lo = mid;
  }
  const to = hi + 1;
  return Math.max(0, to - from);
}

function buildTooltip(
  unitLabel: string | undefined,
  fontSize: number | undefined,
  formatX: (value: number) => string,
) {
  return {
    trigger: "axis",
    backgroundColor: "rgba(15, 23, 42, 0.92)",
    borderRadius: 10,
    padding: [10, 12],
    textStyle: {
      color: "#e2e8f0",
      fontSize: fontSize ?? 14,
    },
    axisPointer: {
      type: "cross",
      animation: false,
    },
    formatter: (params: unknown) => {
      if (!Array.isArray(params) || !params.length) return "";
      const items = params as TooltipItem[];
      const rawX = Number(items[0].value[0]);
      const xLabel = Number.isFinite(rawX) ? formatX(rawX) : "";
      const rows = items
        .map((item) => {
          const value = numberFormatter.format(item.value[1]);
          const unit = unitLabel ? ` ${unitLabel}` : "";
          const color = item.color;
          return `<div style="display:flex;align-items:center;gap:8px;line-height:1.4">
            <span style="display:inline-block;width:10px;height:10px;border-radius:999px;background:${color}"></span>
            <span style="flex:1;">${item.seriesName}</span>
            <strong style="font-variant-numeric:tabular-nums;">${value}${unit}</strong>
          </div>`;
        })
        .join("");
      return `<div style="min-width:220px;display:grid;gap:8px">
        <div style="font-weight:600;">${xLabel}</div>
        ${rows}
      </div>`;
    },
  };
}

function getViewportOrExtent(viewport: Viewport | null | undefined, extent: Extent | null | undefined) {
  if (viewport) return viewport;
  if (extent) return { start: extent.min, end: extent.max };
  return null;
}

function pickStrategy(visibleCount: number, targetPoints: number) {
  if (visibleCount > targetPoints * 3) return "minmax" as const;
  return "lttb" as const;
}

export function TimeSeriesChart({
  series,
  height = 500,
  yLabel = "",
  gridBottom,
  yDomain,
  safeBand,
  extent = null,
  viewport = null,
  onViewportChange,
  unitLabel,
  tooltipFontSize = 14,
  showLegend,
  xAxisType = "time",
  xAxisName,
  tooltipXFormatter,
  dataZoomSlider,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const echartsRef = useRef<EChartsInstance | null>(null);

  const cacheRef = useRef(new DownsampleCache());
  const seriesVersionRef = useRef(new Map<string, string>());
  const viewportChangeRaf = useRef<number | null>(null);
  const pendingViewport = useRef<Viewport | null>(null);

  const [containerWidth, setContainerWidth] = useState(() => {
    if (typeof window === "undefined") return 800;
    return Math.max(200, Math.floor(containerRef.current?.clientWidth ?? 800));
  });
  const [internalViewport, setInternalViewport] = useState<Viewport | null>(() =>
    getViewportOrExtent(viewport, extent),
  );

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const nextWidth = Math.max(200, Math.floor(entry.contentRect.width));
      setContainerWidth((prev) => (prev === nextWidth ? prev : nextWidth));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const next = getViewportOrExtent(viewport, extent);
    setInternalViewport((prev) => (areViewportsClose(prev, next) ? prev : next));
  }, [viewport?.start, viewport?.end, extent?.min, extent?.max]);

  const normalized = useMemo(() => normalizeSeries(series), [series]);

  useEffect(() => {
    const versionMap = seriesVersionRef.current;
    normalized.forEach((serie) => {
      const previous = versionMap.get(serie.id);
      if (previous !== serie.version) {
        cacheRef.current.clearSeries(serie.id);
        versionMap.set(serie.id, serie.version);
      }
    });
  }, [normalized]);

  useEffect(
    () => () => {
      if (viewportChangeRaf.current != null) {
        cancelAnimationFrame(viewportChangeRaf.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!echartsRef.current) return;
    echartsRef.current.resize({ width: containerWidth, height });
  }, [containerWidth, height]);

  const effectiveViewport = getViewportOrExtent(internalViewport, extent);
  const targetPoints = useMemo(() => clampTargetPoints(containerWidth), [containerWidth]);

  const downsampledSeries = useMemo(() => {
    const cache = cacheRef.current;
    return normalized.map((serie) => {
      const visibleCount = countVisible(serie.points, effectiveViewport);
      const strategy = pickStrategy(visibleCount, targetPoints);
      const points = downsampleSeries(
        { id: serie.id, points: serie.points, version: serie.version },
        { targetPoints, strategy, viewport: effectiveViewport },
        cache,
      );
      return {
        ...serie,
        points,
      };
    });
  }, [normalized, effectiveViewport?.start, effectiveViewport?.end, targetPoints]);

  const option = useMemo<EChartsOption>(() => {
    const hasLegend = showLegend ?? series.length > 1;
    const [xMin, xMax] = effectiveViewport
      ? [effectiveViewport.start, effectiveViewport.end]
      : extent
      ? [extent.min, extent.max]
      : [undefined, undefined];
    const safeXMin = typeof xMin === "number" && Number.isFinite(xMin) ? xMin : undefined;
    const safeXMax = typeof xMax === "number" && Number.isFinite(xMax) ? xMax : undefined;

    const yMin = yDomain ? yDomain[0] : "dataMin";
    const yMax = yDomain ? yDomain[1] : "dataMax";

    const formatXAxisValue =
      tooltipXFormatter ??
      (xAxisType === "time"
        ? (value: number) => fallbackFormatter.format(new Date(value))
        : (value: number) => numberFormatter.format(value));

    const seriesOptions: SeriesOption[] = downsampledSeries.map((serie) => ({
      id: serie.id,
      name: serie.name,
      type: "line",
      animation: false,
      showSymbol: false,
      smooth: false,
      z: 2,
      lineStyle: {
        width: 1.6,
        color: serie.color,
      },
      emphasis: { focus: "series" },
      data: serie.points.map((p) => [p.x, p.y]),
    }));

    const hasSafeBand =
      safeBand &&
      Number.isFinite(safeBand.min) &&
      Number.isFinite(safeBand.max) &&
      safeBand.min < safeBand.max &&
      seriesOptions.length > 0;
    if (hasSafeBand) {
      const first = seriesOptions[0] as SeriesOption & { markArea?: Record<string, unknown> };
      first.markArea = {
        silent: true,
        itemStyle: {
          color: safeBand.color ?? "#bbf7d0",
          opacity: safeBand.opacity ?? 0.2,
        },
        data: [[{ yAxis: safeBand.min }, { yAxis: safeBand.max }]],
      };
      seriesOptions[0] = first;
    }

    return {
      animation: false,
      grid: { left: 64, right: 16, top: 20, bottom: gridBottom ?? 54 },
      legend: hasLegend
        ? {
            top: 4,
            padding: [8, 8, 0, 8],
          }
        : undefined,
      tooltip: buildTooltip(unitLabel, tooltipFontSize, formatXAxisValue),
      xAxis: {
        type: xAxisType,
        name: xAxisName,
        min: safeXMin,
        max: safeXMax,
        axisLabel: {
          formatter: (value: number) =>
            xAxisType === "time" ? fallbackFormatter.format(new Date(value)) : numberFormatter.format(value),
        },
      },
      yAxis: {
        type: "value",
        name: yLabel,
        min: yMin as number | "dataMin",
        max: yMax as number | "dataMax",
        axisLabel: { formatter: (value: number) => numberFormatter.format(value) },
        splitLine: { show: true },
      },
      dataZoom: [
        {
          type: "inside",
          filterMode: "none",
          moveOnMouseMove: true,
          zoomOnMouseWheel: true,
          startValue: safeXMin,
          endValue: safeXMax,
          minValueSpan: 1,
        },
        {
          type: "slider",
          filterMode: "none",
          height: dataZoomSlider?.height ?? 28,
          bottom: dataZoomSlider?.bottom ?? 8,
          startValue: safeXMin,
          endValue: safeXMax,
          fillerColor: dataZoomSlider?.fillerColor,
          backgroundColor: dataZoomSlider?.backgroundColor,
          borderColor: dataZoomSlider?.borderColor,
          dataBackground: {
            lineStyle: {
              color: dataZoomSlider?.dataBackgroundLineColor,
            },
            areaStyle: {
              color: dataZoomSlider?.dataBackgroundAreaColor,
            },
          },
          selectedDataBackground: {
            lineStyle: {
              color: dataZoomSlider?.selectedDataBackgroundLineColor,
            },
            areaStyle: {
              color: dataZoomSlider?.selectedDataBackgroundAreaColor,
            },
          },
          handleStyle: {
            color: dataZoomSlider?.handleColor,
            borderColor: dataZoomSlider?.handleBorderColor,
          },
          moveHandleStyle: {
            color: dataZoomSlider?.moveHandleColor,
          },
        },
      ],
      series: seriesOptions,
      color: series.map((item) => item.color).filter(Boolean) as string[],
    };
  }, [
    downsampledSeries,
    effectiveViewport?.start,
    effectiveViewport?.end,
    extent,
    gridBottom,
    series,
    showLegend,
    tooltipFontSize,
    tooltipXFormatter,
    unitLabel,
    dataZoomSlider,
    xAxisName,
    xAxisType,
    yDomain,
    yLabel,
    safeBand,
  ]);

  const handleDataZoom = (event: unknown) => {
    const input = event as DataZoomPayload;
    const payload = Array.isArray(input.batch) ? input.batch[0] : input;
    // Only accept absolute axis values. Percentages (start/end) will map to ~1970 on a time axis.
    const startValue = payload?.startValue;
    const endValue = payload?.endValue;
    if (!Number.isFinite(startValue) || !Number.isFinite(endValue)) return;

    const next: Viewport = { start: Number(startValue), end: Number(endValue) };
    setInternalViewport((prev) => (areViewportsClose(prev, next) ? prev : next));

    if (onViewportChange) {
      pendingViewport.current = next;
      if (viewportChangeRaf.current == null) {
        viewportChangeRaf.current = requestAnimationFrame(() => {
          viewportChangeRaf.current = null;
          if (pendingViewport.current) onViewportChange(pendingViewport.current);
        });
      }
    }
  };

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      <ReactECharts
        style={{ width: "100%", height }}
        option={option}
        notMerge
        lazyUpdate
        autoResize={false}
        onEvents={{ datazoom: handleDataZoom }}
        onChartReady={(instance) => {
          echartsRef.current = instance;
        }}
        opts={{ renderer: "canvas" }}
      />
    </div>
  );
}

export default TimeSeriesChart;
